import { supabase, isSupabaseConfigured } from "./supabase";

const BUCKET = "property-assets";

/**
 * Map DB row (snake_case) to frontend shape (camelCase).
 * image = first of image_urls; pdfs = object from jsonb.
 */
function rowToProperty(row) {
  if (!row) return null;
  const imageUrls = Array.isArray(row.image_urls) ? row.image_urls : [];
  const mainImage = imageUrls[0] || null;
  const pdfs = row.pdfs && typeof row.pdfs === "object" ? row.pdfs : {};
  return {
    id: row.id,
    name: row.name,
    city: row.city,
    community: row.community,
    location: row.location,
    price: row.price,
    priceValue: row.price_value ?? undefined,
    type: row.type ?? row.name,
    units: row.units ?? undefined,
    status: row.status,
    dscr: row.dscr,
    capRate: row.cap_rate,
    cashOnCash: row.cash_on_cash,
    roi: row.roi,
    roiAmount: row.roi_amount,
    cashFlow: row.cash_flow,
    downPayment: row.down_payment,
    monthlyRent: row.monthly_rent,
    equityGrowth: row.equity_growth,
    closingOn: row.closing_on,
    lendingType: row.lending_type,
    depositDetails: row.deposit_details,
    description: row.description,
    yearBuilt: row.year_built,
    squareFeet: row.square_feet,
    lotSize: row.lot_size,
    parking: row.parking,
    heating: row.heating,
    cooling: row.cooling,
    features: Array.isArray(row.features) ? row.features : [],
    image: mainImage,
    images: imageUrls,
    pdfs,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Map form/UI payload to DB row (snake_case, arrays for features/image_urls, object for pdfs).
 */
function propertyToRow(p) {
  return {
    name: p.name,
    city: p.city,
    community: p.community,
    location: p.location,
    price: p.price,
    price_value: p.priceValue ? Number(p.priceValue) : null,
    type: p.type || null,
    units: p.units ? Number(p.units) : null,
    status: p.status || "Available",
    dscr: p.dscr || null,
    cap_rate: p.capRate || null,
    cash_on_cash: p.cashOnCash || null,
    roi: p.roi || null,
    roi_amount: p.roiAmount || null,
    cash_flow: p.cashFlow || null,
    down_payment: p.downPayment || null,
    monthly_rent: p.monthlyRent || null,
    equity_growth: p.equityGrowth || null,
    closing_on: p.closingOn || null,
    lending_type: p.lendingType || null,
    deposit_details: p.depositDetails || null,
    description: p.description || null,
    year_built: p.yearBuilt || null,
    square_feet: p.squareFeet || null,
    lot_size: p.lotSize || null,
    parking: p.parking || null,
    heating: p.heating || null,
    cooling: p.cooling || null,
    features: Array.isArray(p.features) ? p.features : [],
    image_urls: Array.isArray(p.imageUrls) ? p.imageUrls : [],
    pdfs: p.pdfs && typeof p.pdfs === "object" ? p.pdfs : {},
  };
}

export async function fetchProperties() {
  if (!isSupabaseConfigured()) return { data: null, error: new Error("Supabase not configured") };
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) return { data: null, error };
  return { data: (data || []).map(rowToProperty), error: null };
}

export async function fetchPropertyById(id) {
  if (!isSupabaseConfigured()) return { data: null, error: new Error("Supabase not configured") };
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("id", id)
    .single();
  if (error) return { data: null, error };
  return { data: rowToProperty(data), error: null };
}

export async function createProperty(payload) {
  if (!isSupabaseConfigured()) return { data: null, error: new Error("Supabase not configured") };
  const row = propertyToRow(payload);
  const { data, error } = await supabase.from("properties").insert(row).select().single();
  if (error) return { data: null, error };
  return { data: rowToProperty(data), error: null };
}

export async function updateProperty(id, payload) {
  if (!isSupabaseConfigured()) return { data: null, error: new Error("Supabase not configured") };
  const row = propertyToRow(payload);
  const { data, error } = await supabase
    .from("properties")
    .update(row)
    .eq("id", id)
    .select()
    .single();
  if (error) return { data: null, error };
  return { data: rowToProperty(data), error: null };
}

export async function deleteProperty(id) {
  if (!isSupabaseConfigured()) return { error: new Error("Supabase not configured") };
  const { error } = await supabase.from("properties").delete().eq("id", id);
  return { error };
}

/**
 * Upload a file to property-assets bucket.
 * path: e.g. "property-id/images/abc.jpg" or "property-id/pdfs/name.pdf"
 * Returns public URL or throws.
 */
export async function uploadPropertyFile(file, path) {
  if (!isSupabaseConfigured()) throw new Error("Supabase not configured");
  const { data, error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: true,
  });
  if (error) throw error;
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
  return urlData.publicUrl;
}

/**
 * Delete a file by path in bucket.
 */
export async function deletePropertyFile(path) {
  if (!isSupabaseConfigured()) return;
  await supabase.storage.from(BUCKET).remove([path]);
}

export { rowToProperty, propertyToRow, isSupabaseConfigured };
